import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  EventAvailable as EventAvailableIcon,
  AttachMoney as AttachMoneyIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useGetEmployeeReportsQuery, useGetEmployeesListQuery } from '../../services/reportsApi';

const EmployeeReports = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState('salary-slip');

  // Get employees list for autocomplete
  const {
    data: employeesList,
    isLoading: isLoadingEmployees
  } = useGetEmployeesListQuery();

  const {
    data: reportData,
    isLoading,
    error,
    refetch
  } = useGetEmployeeReportsQuery({
    employeeId: selectedEmployee?.employeeId,
    month: selectedMonth,
    year: selectedYear,
    reportType
  }, {
    skip: !selectedEmployee?.employeeId
  });

  useEffect(() => {
    if (selectedEmployee?.employeeId) {
      refetch();
    }
  }, [selectedEmployee, selectedMonth, selectedYear, reportType, refetch]);

  const getApiBase = () => process.env.REACT_APP_API_URL || '/api';
  const getAuthToken = () => localStorage.getItem('token');

  const handleExportReport = (format = 'pdf') => {
    if (!selectedEmployee?.employeeId) {
      alert('Please select an employee first');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      alert('Authentication token missing. Please login again.');
      return;
    }

    if (format === 'pdf') {
      let endpoint = '';
      switch (reportType) {
        case 'salary-slip':
          endpoint = `/reports/download/salary-slip/${selectedEmployee.employeeId}/${selectedMonth}/${selectedYear}`;
          break;
        case 'benefit-summary':
          endpoint = `/reports/download/benefits-summary/${selectedEmployee.employeeId}`;
          break;
        default:
          alert('PDF export not available for this report type');
          return;
      }

      const url = `${getApiBase()}${endpoint}`;
      fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (!res.ok) {
            return res.text().then(text => {
              console.error('PDF download failed:', text);
              throw new Error(`Failed to download PDF: ${res.status} ${res.statusText}`);
            });
          }
          return res.blob();
        })
        .then(blob => {
          console.log('PDF blob received:', blob.size, 'bytes, type:', blob.type);
          
          // Validate blob is actually a PDF
          if (blob.type !== 'application/pdf' && blob.type !== 'application/octet-stream') {
            console.warn('Unexpected blob type:', blob.type);
          }
          
          const fileUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = `${reportType}-${selectedEmployee.employeeId}-${selectedMonth}-${selectedYear}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileUrl);
        })
        .catch(err => {
          console.error('PDF export error:', err);
          alert(`PDF export failed: ${err.message}`);
        });
    } else if (format === 'csv') {
      const params = new URLSearchParams({
        employeeId: selectedEmployee.employeeId,
        month: selectedMonth,
        year: selectedYear,
        reportType,
        format: 'csv'
      });
      const url = `${getApiBase()}/reports/employee-reports?${params.toString()}`;
      fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to download CSV');
          return res.blob();
        })
        .then(blob => {
          const fileUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
            link.href = fileUrl;
          link.download = `${reportType}-${selectedEmployee.employeeId}-${selectedMonth}-${selectedYear}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileUrl);
        })
        .catch(err => {
          console.error(err);
          alert('CSV export failed');
        });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const reportTypes = [
    { value: 'salary-slip', label: 'Salary Slip' },
    { value: 'annual-statement', label: 'Annual Salary Statement' },
    { value: 'benefit-summary', label: 'Benefit Summary' },
    { value: 'leave-attendance', label: 'Leave & Attendance Report' },
  ];

  // Mock data structure for display
  const mockData = {
    employee: {
      employeeId: 'EMP001',
      name: 'John Doe',
      designation: 'Software Engineer',
      department: 'Engineering',
      joiningDate: '2023-01-15',
      email: 'john.doe@company.com'
    },
    salarySlip: {
      month: 'September',
      year: 2025,
      basicSalary: 50000,
      hra: 25000,
      allowances: [
        { name: 'Transport Allowance', amount: 2000 },
        { name: 'Medical Allowance', amount: 1500 },
        { name: 'Special Allowance', amount: 10000 }
      ],
      grossEarnings: 88500,
      deductions: [
        { name: 'PF Employee', amount: 6000 },
        { name: 'ESI Employee', amount: 708 },
        { name: 'Professional Tax', amount: 200 },
        { name: 'TDS', amount: 2500 }
      ],
      totalDeductions: 9408,
      netSalary: 79092,
      workingDays: 22,
      presentDays: 20,
      leaveDays: 2,
      overtimeHours: 0
    },
    benefits: {
      pfEmployee: 6000,
      pfEmployer: 6000,
      esiEmployee: 708,
      esiEmployer: 2363,
      gratuity: 2404,
      bonus: 4167,
      medicalInsurance: 5000,
      totalBenefits: 26642
    },
    leaveAttendance: {
      totalLeavesTaken: 8,
      totalLeavesAvailable: 24,
      casualLeave: { taken: 3, available: 12 },
      sickLeave: { taken: 2, available: 12 },
      earnedLeave: { taken: 3, available: 0 },
      attendancePercentage: 95.5,
      averageWorkingHours: 8.5
    }
  };

  const data = reportData || mockData;

  // Ensure data structure is always valid
  const safeData = {
    employee: data?.employee || mockData.employee,
    salarySlip: data?.salarySlip || mockData.salarySlip,
    benefits: data?.benefits || mockData.benefits,
    leaveAttendance: data?.leaveAttendance || mockData.leaveAttendance
  };

  if (isLoadingEmployees) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const employees = employeesList || [
    { employeeId: 'EMP001', name: 'John Doe', department: 'Engineering' },
    { employeeId: 'EMP002', name: 'Jane Smith', department: 'HR' },
    { employeeId: 'EMP003', name: 'Mike Johnson', department: 'Finance' }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 25%, #e8eaf6 100%)',
      p: 3
    }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
          background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 50%, #6366f1 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 2
        }}
      >
        Employee Reports
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        paragraph
        sx={{ mb: 3, fontSize: '1.1rem' }}
      >
        Individual employee salary and benefit reports with detailed breakdowns
      </Typography>

      {/* Filters */}
      <Paper sx={{
        p: 3,
        mb: 3,
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              value={selectedEmployee}
              onChange={(event, newValue) => {
                console.log('Employee selected:', newValue);
                setSelectedEmployee(newValue);
              }}
              options={employees || []}
              getOptionLabel={(option) => option ? `${option.name} (${option.employeeId})` : ''}
              isOptionEqualToValue={(option, value) => option?.employeeId === value?.employeeId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  variant="outlined"
                  fullWidth
                  error={!selectedEmployee}
                  helperText={!selectedEmployee ? 'Please select an employee' : ''}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.employeeId}>
                  <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                    {option.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.employeeId} • {option.department}
                    </Typography>
                  </Box>
                </Box>
              )}
              loading={isLoadingEmployees}
              disabled={isLoadingEmployees}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthNames.map((month, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {[2023, 2024, 2025, 2026].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportReport('csv')}
              fullWidth
              sx={{
                height: '56px',
                borderRadius: '16px',
                border: '2px solid',
                borderColor: 'primary.main',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  color: 'white',
                  borderColor: 'transparent',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
              disabled={!selectedEmployee}
            >
              CSV
            </Button>
          </Grid>

          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportReport('pdf')}
              fullWidth
              sx={{
                height: '56px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
              disabled={!selectedEmployee}
            >
              PDF
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!selectedEmployee ? (
        <Paper sx={{
          p: 4,
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <PersonIcon sx={{
            fontSize: 64,
            color: 'text.secondary',
            mb: 2,
            opacity: 0.6
          }} />
          <Typography variant="h6" color="text.secondary">
            Please select an employee to view reports
          </Typography>
        </Paper>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          Error loading employee report: {error.message}
        </Alert>
      ) : (
        <>
          {/* Employee Info Header */}
          <Paper sx={{
            p: 3,
            mb: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                }}>
                  {safeData.employee.name.split(' ').map(n => n[0]).join('')}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" gutterBottom sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600
                }}>
                  {safeData.employee.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {safeData.employee.designation} • {safeData.employee.department}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Employee ID: {safeData.employee.employeeId} • Joined: {safeData.employee.joiningDate}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  icon={<DateRangeIcon />}
                  label={`${monthNames[selectedMonth - 1]} ${selectedYear}`}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                    color: 'white',
                    borderRadius: '16px',
                    fontWeight: 500,
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Report Content Based on Type */}
          {reportType === 'salary-slip' && (
            <Grid container spacing={3}>
              {/* Earnings Section */}
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  height: '100%'
                }}>
                  <Typography variant="h6" gutterBottom sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600
                  }}>
                    <AttachMoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                    Earnings
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Basic Salary</TableCell>
                        <TableCell align="right">{formatCurrency(safeData.salarySlip.basicSalary)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>HRA</TableCell>
                        <TableCell align="right">{formatCurrency(safeData.salarySlip.hra)}</TableCell>
                      </TableRow>
                      {safeData.salarySlip.allowances.map((allowance, index) => (
                        <TableRow key={index}>
                          <TableCell>{allowance.name}</TableCell>
                          <TableCell align="right">{formatCurrency(allowance.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '8px'
                      }}>
                        <TableCell sx={{ fontWeight: 600 }}>Gross Earnings</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(safeData.salarySlip.grossEarnings)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Deductions Section */}
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  height: '100%'
                }}>
                  <Typography variant="h6" gutterBottom sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600
                  }}>
                    <ReceiptIcon sx={{ mr: 1, color: 'error.main' }} />
                    Deductions
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {safeData.salarySlip.deductions.map((deduction, index) => (
                        <TableRow key={index}>
                          <TableCell>{deduction.name}</TableCell>
                          <TableCell align="right">{formatCurrency(deduction.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '8px'
                      }}>
                        <TableCell sx={{ fontWeight: 600 }}>Total Deductions</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(safeData.salarySlip.totalDeductions)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Net Salary & Attendance */}
              <Grid item xs={12}>
                <Paper sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 700,
                        fontSize: '1.5rem'
                      }}>
                        Net Salary: {formatCurrency(safeData.salarySlip.netSalary)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gross Earnings - Total Deductions
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body1" gutterBottom sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 3
                      }}>
                        Attendance Summary
                      </Typography>

                      {/* Modern Attendance Cards */}
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Box sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                            borderRadius: '16px',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)'
                            }
                          }}>
                            <Typography variant="h4" sx={{
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}>
                              {safeData.salarySlip.workingDays}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Working Days
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                            borderRadius: '16px',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                            }
                          }}>
                            <Typography variant="h4" sx={{
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}>
                              {safeData.salarySlip.presentDays}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Present Days
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box sx={{
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                            borderRadius: '16px',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                            }
                          }}>
                            <Typography variant="h4" sx={{
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}>
                              {safeData.salarySlip.leaveDays}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Leave Days
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Attendance Percentage */}
                      <Box sx={{
                        mt: 3,
                        p: 3,
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                        borderRadius: '16px',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Attendance Rate
                        </Typography>
                        <Typography variant="h3" sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          {Math.round((safeData.salarySlip.presentDays / Math.max(safeData.salarySlip.workingDays, 1)) * 100)}%
                        </Typography>
                        <Box sx={{
                          mt: 2,
                          height: '8px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: `${(safeData.salarySlip.presentDays / Math.max(safeData.salarySlip.workingDays, 1)) * 100}%`,
                            background: 'linear-gradient(90deg, #8b5cf6 0%, #a855f7 100%)',
                            borderRadius: '4px',
                            transition: 'width 0.5s ease-in-out'
                          }} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}

          {reportType === 'benefit-summary' && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}>
                  <Typography variant="h6" gutterBottom sx={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600
                  }}>
                    Employee Benefits
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Provident Fund (Employee)"
                        secondary={formatCurrency(safeData.benefits.pfEmployee)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Provident Fund (Employer)"
                        secondary={formatCurrency(safeData.benefits.pfEmployer)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="ESI (Employee)"
                        secondary={formatCurrency(safeData.benefits.esiEmployee)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="ESI (Employer)"
                        secondary={formatCurrency(safeData.benefits.esiEmployer)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Gratuity"
                        secondary={formatCurrency(safeData.benefits.gratuity)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Medical Insurance"
                        secondary={formatCurrency(safeData.benefits.medicalInsurance)}
                      />
                    </ListItem>
                  </List>
                  <Divider />
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <Typography variant="h6" sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 700
                    }}>
                      Total Benefits: {formatCurrency(safeData.benefits.totalBenefits)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {reportType === 'leave-attendance' && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}>
                  <Typography variant="h6" gutterBottom sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600
                  }}>
                    <EventAvailableIcon sx={{ mr: 1 }} />
                    Leave Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">Casual Leave</Typography>
                        <Typography variant="h6" sx={{
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 600
                        }}>
                          {safeData.leaveAttendance.casualLeave.taken}/{safeData.leaveAttendance.casualLeave.available}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">Sick Leave</Typography>
                        <Typography variant="h6" sx={{
                          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 600
                        }}>
                          {safeData.leaveAttendance.sickLeave.taken}/{safeData.leaveAttendance.sickLeave.available}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">Earned Leave</Typography>
                        <Typography variant="h6" sx={{
                          background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 600
                        }}>
                          {safeData.leaveAttendance.earnedLeave.taken}/{safeData.leaveAttendance.earnedLeave.available}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="body2" color="text.secondary">Attendance %</Typography>
                        <Typography variant="h6" sx={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 600
                        }}>
                          {safeData.leaveAttendance.attendancePercentage}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default EmployeeReports;